import { graphql } from '~/graphql';
import type { EmailDeliveryTraceFilter } from '~/graphql/graphql';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import {
  usePaginatedQuery,
  type PaginatedFetchOptions,
  type RouteSort,
} from '~/hooks/usePaginatedQuery';

const EMAIL_DELIVERY_TRACES_QUERY_KEY = 'emailDeliveryTraces';

export const emailDeliveryTracesQuery = graphql(`
  query EmailDeliveryTraces(
    $filter: EmailDeliveryTraceFilter
    $pageInput: PageInput!
  ) {
    emailDeliveryTraces(filter: $filter, pageInput: $pageInput) {
      content {
        id
        emailType
        recipientTo
        recipientCc
        status
        lastError
        sesMessageId
        deliveryOutcome
        deliveryOutcomeAt
        createdAt
        sentAt
        events {
          id
          eventType
          recipient
          bounceType
          bounceSubType
          diagnosticCode
          reason
          occurredAt
        }
      }
      paging {
        number
        size
        totalElements
        totalPages
      }
    }
  }
`);

export const sendPlatformTestEmailMutation = graphql(`
  mutation SendPlatformTestEmail($input: SendPlatformTestEmailInput!) {
    sendPlatformTestEmail(input: $input) {
      id
      recipientTo
      sesMessageId
      deliveryOutcome
      createdAt
    }
  }
`);

const useEmailDeliveryTraces = (
  page: number,
  pageSize: number,
  filter?: EmailDeliveryTraceFilter,
  _sort?: RouteSort,
  options?: PaginatedFetchOptions
) =>
  useGraphqlQuery(
    emailDeliveryTracesQuery,
    [
      EMAIL_DELIVERY_TRACES_QUERY_KEY,
      String(page),
      String(pageSize),
      JSON.stringify(filter ?? null),
    ],
    { filter, pageInput: { page, size: pageSize } },
    options
  );

const EMPTY_PAGING = { number: 0, size: 0, totalElements: 0, totalPages: 0 };

export const useEmailDeliveryTracesPaginated = (
  pageSize: number,
  filter?: EmailDeliveryTraceFilter
) => {
  const result = usePaginatedQuery({
    fetchHook: useEmailDeliveryTraces,
    select: (data) => data?.emailDeliveryTraces,
    options: { pageSize, filters: filter },
  });

  return { ...result, pagination: result.pagination ?? EMPTY_PAGING };
};

export const useSendPlatformTestEmail = () =>
  useGraphqlMutation(sendPlatformTestEmailMutation, [
    EMAIL_DELIVERY_TRACES_QUERY_KEY,
  ]);
